/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text, Title } from '@/shared/ui';
import type { SettingsSection } from '../constants/settings-nav';

/**
 * The active section's title and the one line that says what it is for.
 *
 * Drawn once here rather than by each section: three sections used to draw
 * their own heading at three different sizes, which is how a reader learns
 * that the heading level means nothing.
 *
 * @param props - Component props.
 * @param props.section - The active section key.
 * @returns The section heading.
 */
export function SettingsSectionHeading({ section }: { section: SettingsSection }) {
  const { t } = useTranslation('features/agency-settings');

  return (
    <div data-testid={`settings-section-heading-${section}`}>
      <Title
        order={2}
        style={{
          margin: 0, fontSize: 'var(--text-app-section)', fontWeight: 700,
          color: 'var(--text-primary)',
        }}
      >
        {t(`v2.sections.${section}.title`)}
      </Title>
      <Text
        style={{
          margin: 'var(--space-1) 0 0', fontSize: 'var(--text-body-sm)',
          color: 'var(--text-tertiary-soft)', lineHeight: 1.5, maxWidth: '72ch',
        }}
      >
        {t(`v2.sections.${section}.description`)}
      </Text>
    </div>
  );
}
